import { createRouter, createWebHistory } from "vue-router/auto";
import { routes } from "vue-router/auto-routes";
import { useAuthStore } from "@/stores/auth";
import { marcarBoot } from "@/lib/boot";

const PUBLIC_PATHS = [
  "/login",
  "/cadastro",
  "/recuperar",
  "/auth/callback",
  "/auth/leiturista",
  // O leiturista NÃO tem sessão Supabase: o token dele vive no localStorage,
  // então auth.isAuthenticated é sempre falso para ele. Quem defende esta tela
  // é o próprio campo/index.vue, que manda para /campo/login sem token.
  // Cobre /campo e /campo/login de uma vez.
  "/campo",
  // download do APK: o leiturista precisa baixar antes de ter onde entrar
  "/app",
  // login/cadastro do morador (público, com auto-cadastro via OTP)
  "/morador/entrar",
];

/**
 * Dentro do APK só existe o app de campo.
 *
 * O aplicativo foi feito para o leiturista e para uma coisa que o navegador não
 * faz: Bluetooth clássico. O painel administrativo não foi desenhado para tela
 * de celular, e abrir nele confunde quem instalou o app para trabalhar. Antes o
 * APK subia na raiz e caía no login do ADMIN.
 */
/** Consultado a cada navegação, nunca no carregamento: a ponte do Capacitor
 *  pode não ter injetado o global ainda quando este módulo é avaliado. */
export function ehNativo(): boolean {
  try {
    return (globalThis as any).Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  marcarBoot("guard:" + to.path);
  const nativo = ehNativo();
  // no aplicativo, qualquer rota fora do campo volta para o campo
  if (nativo && !to.path.startsWith("/campo")) {
    return { path: "/campo", replace: true };
  }

  const isPublic = PUBLIC_PATHS.some((p) => to.path === p || to.path.startsWith(p + "/"));
  const auth = useAuthStore();

  /**
   * Rota pública NUNCA espera a rede.
   *
   * auth.init() chama supabase.auth.getSession(), que pode tentar renovar o
   * token pela rede. Esperar por isso aqui travava a navegação inteira quando o
   * sinal estava ruim, e como o <RouterView> não renderiza nada enquanto a
   * navegação não resolve, o app abria em tela branca. O leiturista nem usa
   * sessão do Supabase: o token dele é local.
   */
  if (isPublic) {
    if (!auth.initialized) auth.init().catch(() => {});   // dispara e segue
    marcarBoot("rota-publica:" + to.path);
    return true;
  }

  // rota privada precisa saber quem é, mas com teto: melhor mandar para o login
  // do que deixar o app parado sem nada na tela
  if (!auth.initialized) {
    await Promise.race([
      auth.init().catch(() => {}),
      new Promise((r) => setTimeout(r, 4000)),
    ]);
  }

  if (!auth.isAuthenticated && !isPublic) {
    // no aplicativo quem entra é o leiturista, com o login dele
    if (to.path.startsWith("/morador")) return { path: "/morador/entrar", query: { next: to.fullPath } };
    return nativo
      ? { path: "/campo/login", query: { next: to.fullPath } }
      : { path: "/login", query: { next: to.fullPath } };
  }
  if (auth.isAuthenticated && to.path === "/login") {
    return { path: auth.appHome ?? "/dashboard" };
  }
  // síndico e morador NÃO acessam o admin: ficam confinados ao próprio app
  if (auth.isAuthenticated && auth.appHome && !to.path.startsWith(auth.appHome)) {
    return { path: auth.appHome, replace: true };
  }
});

export default router;
